'use client'

import { useAppRouter } from '@/shared/lib/router'
import { UnavailableState } from '@/shared/ui/UnavailableState'

import { useLevelContext } from '../contexts/LevelContext'
import { useNextLevelTarget } from '../hooks/useNextLevelTarget'

type LevelRedoBlockedStateProps = {
  title: string
  description: string
  onBack: () => void
}

/**
 * A journey that blocks redo used to leave the student on a dead end ("volte
 * para escolher outro nível"). Since the level is finished, the useful move is
 * the next one — so the screen says what happened and hands over the way
 * forward, keeping the way back as the secondary action.
 */
export function LevelRedoBlockedState({
  title,
  description,
  onBack
}: LevelRedoBlockedStateProps) {
  const router = useAppRouter()
  const { level } = useLevelContext()
  const nextAction = useNextLevelTarget(
    level?.uid ?? null,
    level?.journey?.id ?? null
  )

  if (nextAction.kind === 'loading') {
    return (
      <UnavailableState
        title={title}
        description={description}
        actionLabel="Preparando próximo nível..."
        actionDisabled
        onAction={() => {}}
        secondaryActionLabel="Voltar"
        onSecondaryAction={onBack}
      />
    )
  }

  if (nextAction.kind === 'next-level') {
    return (
      <UnavailableState
        title={title}
        description={`${description} Você pode seguir para o próximo nível.`}
        actionLabel="Ir para o próximo nível"
        onAction={() => router.push(`/niveis/${nextAction.targetLevelUid}`)}
        secondaryActionLabel="Voltar"
        onSecondaryAction={onBack}
      />
    )
  }

  return (
    <UnavailableState
      title={title}
      description={
        nextAction.kind === 'journey-finished'
          ? `${description} Você já concluiu os níveis disponíveis desta jornada.`
          : description
      }
      onAction={onBack}
    />
  )
}
